import React, { useState, useEffect, useCallback } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  getCountFromServer,
  doc,
  getDoc,
  setDoc,
  collectionGroup,
  addDoc,
} from "firebase/firestore";
import { db, storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import { COLORS } from "../constants";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./Schools.css";

const Schools = () => {
  const [metrics, setMetrics] = useState({
    totalSchools: 0,
    totalUsers: 0,
    usersWithSchool: 0,
    usersWithoutSchool: 0,
    schoolBreakdown: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showAllSchools, setShowAllSchools] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [previewSchoolName, setPreviewSchoolName] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [shareableLink, setShareableLink] = useState(null);
  const [trackingLink, setTrackingLink] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [trackingId, setTrackingId] = useState(null);

  const loadCachedMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load cached metrics from analytics/schools document
      const schoolsDocRef = doc(db, "analytics", "schools");
      const schoolsDoc = await getDoc(schoolsDocRef);

      if (schoolsDoc.exists()) {
        const data = schoolsDoc.data();
        setMetrics({
          totalSchools: data.totalSchools || 0,
          totalUsers: data.totalUsers || 0,
          usersWithSchool: data.usersWithSchool || 0,
          usersWithoutSchool: data.usersWithoutSchool || 0,
          schoolBreakdown: data.schoolBreakdown || [],
        });
        setLastUpdated(data.lastUpdated?.toDate() || null);
        console.log(
          "Loaded cached schools metrics from:",
          data.lastUpdated?.toDate()
        );
      } else {
        console.log("No cached metrics found, calculating fresh...");
        await calculateAndSaveMetrics();
      }
    } catch (err) {
      console.error("Error loading cached metrics:", err);
      setError("Failed to load metrics. Please try again.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadCachedMetrics();
  }, [loadCachedMetrics]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await calculateAndSaveMetrics();
    setIsRefreshing(false);
  };

  const calculateAndSaveMetrics = async () => {
    try {
      if (!isRefreshing) {
        setLoading(true);
      }
      setError(null);

      const usersRef = collection(db, "users");

      // Get total user count using count aggregation (doesn't read documents)
      let totalUsers = 0;
      try {
        const totalCountSnapshot = await getCountFromServer(usersRef);
        totalUsers = totalCountSnapshot.data().count;
        console.log(`Total users (from count): ${totalUsers}`);
      } catch (countError) {
        console.warn(
          "Count aggregation not available, falling back to reading all users for count"
        );
        // Fallback: if count() is not available, we'll get count from the query below
        const allUsersSnapshot = await getDocs(usersRef);
        totalUsers = allUsersSnapshot.size;
        console.log(`Total users (from getDocs): ${totalUsers}`);
      }

      // Query only users that have a school field (non-empty)
      // This is much more efficient than reading all users
      const usersWithSchoolQuery = query(usersRef, where("school", ">", ""));
      const usersWithSchoolSnapshot = await getDocs(usersWithSchoolQuery);

      console.log(`Users with school found: ${usersWithSchoolSnapshot.size}`);

      const usersWithSchool = usersWithSchoolSnapshot.size;
      const usersWithoutSchool = totalUsers - usersWithSchool;
      const schoolCountMap = new Map();

      // Process only users with school to build breakdown
      usersWithSchoolSnapshot.forEach((userDoc) => {
        const userData = userDoc.data();
        const schoolName = userData.school?.trim() || "";

        if (schoolName) {
          // Skip "other" schools (case-insensitive) - they shouldn't affect rankings
          const schoolNameLower = schoolName.toLowerCase();
          if (schoolNameLower === "other" || schoolNameLower === "other school" || schoolNameLower === "other university") {
            return; // Skip this school
          }

          // Count users per school
          if (schoolCountMap.has(schoolName)) {
            schoolCountMap.set(schoolName, schoolCountMap.get(schoolName) + 1);
          } else {
            schoolCountMap.set(schoolName, 1);
          }
        }
      });

      // Convert map to array, filter out "other" schools, and sort by count (descending)
      const schoolBreakdown = Array.from(schoolCountMap.entries())
        .map(([name, count]) => ({
          name,
          count,
        }))
        .filter((school) => {
          // Double-check: filter out any "other" schools (case-insensitive)
          const nameLower = school.name.toLowerCase();
          return nameLower !== "other" && nameLower !== "other school" && nameLower !== "other university";
        })
        .sort((a, b) => b.count - a.count);

      const totalSchools = schoolCountMap.size;

      console.log("=== Schools Metrics Summary ===");
      console.log(`Total users: ${totalUsers}`);
      console.log(`Users with school: ${usersWithSchool}`);
      console.log(`Users without school: ${usersWithoutSchool}`);
      console.log(`Total unique schools: ${totalSchools}`);
      console.log("Top schools:", schoolBreakdown.slice(0, 5));

      const metricsData = {
        totalSchools,
        totalUsers,
        usersWithSchool,
        usersWithoutSchool,
        schoolBreakdown,
        lastUpdated: new Date(),
      };

      setMetrics(metricsData);
      setLastUpdated(new Date());

      // Save metrics to analytics/schools document
      const schoolsDocRef = doc(db, "analytics", "schools");
      await setDoc(schoolsDocRef, {
        ...metricsData,
        lastUpdated: new Date(),
      });

      console.log("✅ Schools metrics saved to analytics/schools");
    } catch (err) {
      console.error("Error calculating schools metrics:", err);
      setError("Failed to calculate schools metrics. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="schools-page">
        <div className="page-header">
          <h1>Schools</h1>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading schools data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="schools-page">
        <div className="page-header">
          <h1>Schools</h1>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={handleRefresh} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const formatLastUpdated = () => {
    if (!lastUpdated) return "";

    const now = new Date();
    const diffMs = now - lastUpdated;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60)
      return `${diffMins} minute${diffMins === 1 ? "" : "s"} ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  };

  // Prepare pie chart data
  // Show only top 3 schools in the pie chart to avoid legend overflow
  const MAX_PIE_SCHOOLS = 3;
  let pieData = [];

  if (metrics.schoolBreakdown.length > 0) {
    // Add "No School" if there are users without school
    if (metrics.usersWithoutSchool > 0) {
      pieData.push({
        name: "No School Configured",
        value: metrics.usersWithoutSchool,
        color: COLORS.gray[400],
      });
    }

    // Add top 3 schools only
    const topSchools = metrics.schoolBreakdown.slice(0, MAX_PIE_SCHOOLS);
    const remainingCount = metrics.schoolBreakdown
      .slice(MAX_PIE_SCHOOLS)
      .reduce((sum, school) => sum + school.count, 0);

    topSchools.forEach((school, index) => {
      pieData.push({
        name: school.name,
        value: school.count,
        color: COLORS.primary,
      });
    });

    // Add "Others" if there are remaining schools
    if (remainingCount > 0) {
      pieData.push({
        name: "Other Schools",
        value: remainingCount,
        color: COLORS.accent,
      });
    }
  }

  // Generate colors for pie chart (use entry color if available, otherwise generate)
  const generateColors = (data) => {
    const baseColors = [
      COLORS.primary,
      COLORS.accent,
      COLORS.success,
      COLORS.info,
      "#8b5cf6",
      "#ec4899",
      "#14b8a6",
      "#f59e0b",
    ];
    return data.map((entry, index) => {
      // Use the color from entry if it exists, otherwise use generated color
      return entry.color || baseColors[index % baseColors.length];
    });
  };

  const pieColors = generateColors(pieData);

  const getSchoolStats = async (schoolName, rank) => {
    try {
      setGeneratingPDF(schoolName);

      // Get all users from this school
      const usersRef = collection(db, "users");
      const schoolUsersQuery = query(
        usersRef,
        where("school", "==", schoolName)
      );
      const schoolUsersSnapshot = await getDocs(schoolUsersQuery);

      const schoolUserIds = new Set();
      let studentsWithSubscriptions = 0;
      let totalStudents = 0;
      let studentsInLast30Days = 0;
      let studentsBefore30Days = 0;

      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      schoolUsersSnapshot.forEach((userDoc) => {
        const userData = userDoc.data();
        schoolUserIds.add(userDoc.id);
        totalStudents++;

        // Check if user has a paid subscription
        const subscriptionStatus = userData.subscriptionStatus;
        if (subscriptionStatus === "active" || subscriptionStatus === "scholar") {
          studentsWithSubscriptions++;
        }

        // Track signups in last 30 days vs before
        const createdAt = userData.createdAt?.toDate?.() || userData.createdAt;
        if (createdAt) {
          if (createdAt >= thirtyDaysAgo) {
            studentsInLast30Days++;
          } else {
            studentsBefore30Days++;
          }
        } else {
          // If no createdAt, assume they existed before
          studentsBefore30Days++;
        }
      });

      // Get courses and assignments for this school's users
      const coursesRef = collectionGroup(db, "courses");
      const assignmentsRef = collectionGroup(db, "assignments");

      const coursesSnapshot = await getDocs(coursesRef);
      const assignmentsSnapshot = await getDocs(assignmentsRef);

      let schoolCourses = 0;
      let schoolAssignments = 0;
      let coursesBefore30Days = 0;
      let assignmentsBefore30Days = 0;

      coursesSnapshot.forEach((courseDoc) => {
        const coursePath = courseDoc.ref.path;
        const userId = coursePath.split("/")[1]; // Extract userId from path

        if (schoolUserIds.has(userId)) {
          schoolCourses++;
          const courseData = courseDoc.data();
          const createdAt = courseData.createdAt?.toDate?.() || courseData.createdAt;

          // Count courses that existed 30 days ago (for comparison)
          if (createdAt && createdAt < thirtyDaysAgo) {
            coursesBefore30Days++;
          }
        }
      });

      // Collect all assignments with grades for grade improvement calculation
      const gradedAssignments = [];

      assignmentsSnapshot.forEach((assignmentDoc) => {
        const assignmentPath = assignmentDoc.ref.path;
        const userId = assignmentPath.split("/")[1]; // Extract userId from path

        if (schoolUserIds.has(userId)) {
          schoolAssignments++;
          const assignmentData = assignmentDoc.data();
          const createdAt = assignmentData.createdAt?.toDate?.() || assignmentData.createdAt;

          // Count assignments that existed 30 days ago (for comparison)
          if (createdAt && createdAt < thirtyDaysAgo) {
            assignmentsBefore30Days++;
          }

          // Collect graded assignments for improvement calculation
          const grade = assignmentData.grade;
          if (grade !== undefined && grade !== null && typeof grade === 'number' && grade >= 0 && createdAt) {
            gradedAssignments.push({
              grade,
              createdAt: createdAt instanceof Date ? createdAt : createdAt.toDate(),
            });
          }
        }
      });

      // Sort assignments by creation date
      gradedAssignments.sort((a, b) => a.createdAt - b.createdAt);

      // Calculate grade improvement: first half vs second half
      let gradeImprovementPercent = 0;
      if (gradedAssignments.length >= 2) {
        const midPoint = Math.floor(gradedAssignments.length / 2);
        const firstHalf = gradedAssignments.slice(0, midPoint);
        const secondHalf = gradedAssignments.slice(midPoint);

        const firstHalfAvg = firstHalf.reduce((sum, a) => sum + a.grade, 0) / firstHalf.length;
        const secondHalfAvg = secondHalf.reduce((sum, a) => sum + a.grade, 0) / secondHalf.length;

        if (firstHalfAvg > 0) {
          gradeImprovementPercent = Math.round(((secondHalfAvg - firstHalfAvg) / firstHalfAvg) * 100);
        }
      }

      // Calculate course growth: (current total) / (total 30 days ago) * 100
      // Total 30 days ago = coursesBefore30Days
      // Current total = schoolCourses
      let courseGrowthPercent = 0;
      if (coursesBefore30Days > 0) {
        courseGrowthPercent = Math.round((schoolCourses / coursesBefore30Days) * 100);
      } else if (schoolCourses > 0) {
        // If no courses existed 30 days ago but we have courses now, show 100%+ growth
        courseGrowthPercent = 100;
      }

      // Calculate assignment growth: (current total) / (total 30 days ago) * 100 (fallback if grade improvement is negative)
      let assignmentGrowthPercent = 0;
      if (assignmentsBefore30Days > 0) {
        assignmentGrowthPercent = Math.round((schoolAssignments / assignmentsBefore30Days) * 100);
      } else if (schoolAssignments > 0) {
        // If no assignments existed 30 days ago but we have assignments now, show 100%+ growth
        assignmentGrowthPercent = 100;
      }

      // Use assignment growth as fallback if grade improvement is negative or less than 10%
      const displayGradeImprovement = (gradeImprovementPercent < 0 || gradeImprovementPercent < 10) 
        ? assignmentGrowthPercent 
        : gradeImprovementPercent;
      const isGradeImprovement = gradeImprovementPercent >= 10;

      // Calculate % of students who will save money (those without subscriptions)
      const studentsWithoutSubscriptions = totalStudents - studentsWithSubscriptions;
      const percentWillSaveMoney = totalStudents > 0
        ? Math.round((studentsWithoutSubscriptions / totalStudents) * 100)
        : 0;

      // Calculate % active students in last 30 days
      // (users signed up in last 30 days) / (users that existed before 30 days ago) * 100
      let percentActiveStudents = 0;
      if (studentsBefore30Days > 0) {
        percentActiveStudents = Math.round((studentsInLast30Days / studentsBefore30Days) * 100);
      } else if (studentsInLast30Days > 0) {
        // If no students existed before but we have new ones, show 100%
        percentActiveStudents = 100;
      }

      return {
        schoolName,
        rank,
        totalSchools: metrics.totalSchools,
        totalStudents,
        percentActiveStudents,
        schoolCourses,
        schoolAssignments,
        courseGrowthPercent,
        gradeImprovementPercent,
        displayGradeImprovement,
        isGradeImprovement,
        assignmentGrowthPercent, // Fallback if grade improvement is negative
        percentWillSaveMoney,
      };
    } catch (err) {
      console.error("Error getting school stats:", err);
      throw err;
    }
  };

  const generateSchoolPDF = async (schoolName, rank) => {
    try {
      // Get current stats first (needed for both caching check and PDF generation)
      const stats = await getSchoolStats(schoolName, rank);
      
      // Create a hash of current stats for comparison
      const currentStatsHash = JSON.stringify({
        totalStudents: stats.totalStudents,
        percentActiveStudents: stats.percentActiveStudents,
        courseGrowthPercent: stats.courseGrowthPercent,
        displayGradeImprovement: stats.displayGradeImprovement,
        isGradeImprovement: stats.isGradeImprovement,
        percentWillSaveMoney: stats.percentWillSaveMoney,
      });

      // Check if PDF already exists for this school
      const existingPdfQuery = query(
        collection(db, "pdfTracking"),
        where("schoolName", "==", schoolName)
      );
      const existingPdfSnapshot = await getDocs(existingPdfQuery);

      if (!existingPdfSnapshot.empty) {
        // Get the most recent PDF
        const existingPdfs = existingPdfSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
        })).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

        const latestPdf = existingPdfs[0];

        // Check if stored stats exist and match
        if (latestPdf.statsHash === currentStatsHash && latestPdf.storageUrl) {
          // Stats haven't changed, reuse existing PDF
          const baseUrl = window.location.origin;
          const trackingUrl = latestPdf.email 
            ? `${baseUrl}/track-pdf/${latestPdf.id}?email=${encodeURIComponent(latestPdf.email)}`
            : `${baseUrl}/track-pdf/${latestPdf.id}`;

          setTrackingId(latestPdf.id);
          setShareableLink(latestPdf.storageUrl);
          setTrackingLink(trackingUrl);
          
          // Create preview URL from storage URL
          const previewUrl = latestPdf.storageUrl;
          setPdfPreview(previewUrl);
          setPreviewSchoolName(schoolName);
          setGeneratingPDF(null);
          
          alert("Using existing PDF (stats haven't changed).");
          return;
        }
      }

      // Create a temporary div to render the PDF content
      const pdfContainer = document.createElement("div");
      pdfContainer.style.position = "absolute";
      pdfContainer.style.left = "-9999px";
      pdfContainer.style.width = "210mm"; // A4 width
      pdfContainer.style.padding = "20mm";
      pdfContainer.style.backgroundColor = "#ffffff";
      pdfContainer.style.fontFamily = "Arial, sans-serif";
      pdfContainer.style.color = "#1a1a1a";
      document.body.appendChild(pdfContainer);

      // Create PDF content
      const logoPath = window.location.origin + "/textLogo.png";
      pdfContainer.innerHTML = `
        <div style="display: flex; flex-direction: column; gap: 30px;">
          <!-- Header with Logo -->
          <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #238a94; padding-bottom: 20px;">
            <div style="display: flex; align-items: center; gap: 12px;">
              <img src="${logoPath}" alt="SyllySync" style="height: 50px; object-fit: contain; max-width: 200px;" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';">
              <div style="display: none; font-size: 24px; font-weight: 700; color: #238a94;">SyllySync</div>
            </div>
            <div style="text-align: right; color: #666; font-size: 14px;">
              <div>School Performance Report</div>
              <div style="margin-top: 4px;">${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
            </div>
          </div>

          <!-- School Name Header -->
          <div style="text-align: center; padding: 30px 0;">
            <h1 style="margin: 0; font-size: 36px; color: #238a94; font-weight: 700;">${stats.schoolName}</h1>
            <div style="margin-top: 10px; font-size: 20px; color: #666; font-weight: 600;">
              Ranked #${stats.rank} out of ${stats.totalSchools} schools
            </div>
          </div>

          <!-- Key Metrics Grid -->
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 25px; margin-top: 20px;">
            <!-- Growth Metrics -->
            <div style="background: linear-gradient(135deg, #238a94 0%, #1d6f77 100%); padding: 30px; border-radius: 12px; color: white;">
              <div style="font-size: 14px; opacity: 0.9; margin-bottom: 10px;">Course Growth</div>
              <div style="font-size: 48px; font-weight: 700; line-height: 1;">${stats.courseGrowthPercent}%</div>
              <div style="font-size: 12px; opacity: 0.8; margin-top: 8px;">vs 30 days ago</div>
            </div>

            <div style="background: linear-gradient(135deg, #FF914D 0%, #e67e3d 100%); padding: 30px; border-radius: 12px; color: white;">
              <div style="font-size: 14px; opacity: 0.9; margin-bottom: 10px;">${stats.isGradeImprovement ? 'Grade Improvement' : 'Assignment Growth'}</div>
              <div style="font-size: 48px; font-weight: 700; line-height: 1;">${stats.isGradeImprovement ? '+' + stats.displayGradeImprovement + '%' : stats.displayGradeImprovement + '%'}</div>
              <div style="font-size: 12px; opacity: 0.8; margin-top: 8px;">${stats.isGradeImprovement ? 'first half vs second half' : 'vs 30 days ago'}</div>
            </div>

            <div style="background: linear-gradient(135deg, #4caf50 0%, #45a049 100%); padding: 30px; border-radius: 12px; color: white;">
              <div style="font-size: 14px; opacity: 0.9; margin-bottom: 10px;">Student Savings Potential</div>
              <div style="font-size: 48px; font-weight: 700; line-height: 1;">${stats.percentWillSaveMoney}%</div>
              <div style="font-size: 12px; opacity: 0.8; margin-top: 8px;">of students will save money</div>
            </div>

            <div style="background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); padding: 30px; border-radius: 12px; color: white;">
              <div style="font-size: 14px; opacity: 0.9; margin-bottom: 10px;">Active Students</div>
              <div style="font-size: 48px; font-weight: 700; line-height: 1;">${stats.percentActiveStudents}%</div>
              <div style="font-size: 12px; opacity: 0.8; margin-top: 8px;">in last 30 days</div>
            </div>
          </div>

          <!-- Impact Statement -->
          <div style="background: #f8f9fa; padding: 25px; border-radius: 12px; border-left: 5px solid #238a94;">
            <h3 style="margin: 0 0 15px 0; font-size: 20px; color: #238a94;">Impact at ${stats.schoolName}</h3>
            <div style="color: #333; line-height: 1.8; font-size: 15px;">
              <p style="margin: 0 0 12px 0;">
                Your students are showing <strong>${stats.courseGrowthPercent}% of previous course activity</strong>${stats.isGradeImprovement ? ` with <strong>${stats.displayGradeImprovement}% grade improvement</strong> from first half to second half of assignments` : ` and <strong>${stats.displayGradeImprovement}% of previous assignment activity</strong>`}, with 
                <strong>${stats.percentActiveStudents}% of your student base</strong> actively engaging with SyllySync in the last 30 days, 
                demonstrating strong adoption and engagement.
              </p>
              <p style="margin: 0;">
                With <strong>${stats.percentWillSaveMoney}% of your students</strong> eligible for savings through 
                our organization subscription, implementing SyllySync at scale will provide significant value to your 
                student body while streamlining academic management.
              </p>
            </div>
          </div>

          <!-- Footer -->
          <div style="margin-top: auto; padding-top: 30px; border-top: 2px solid #e5e7eb; text-align: center; color: #666; font-size: 12px;">
            <div>Generated by SyllySync Admin Dashboard</div>
            <div style="margin-top: 5px;">For more information, contact your SyllySync representative</div>
          </div>
        </div>
      `;

      // Wait for image to load and ensure it's rendered
      const logoImg = pdfContainer.querySelector('img');
      if (logoImg) {
        await new Promise((resolve) => {
          if (logoImg.complete) {
            setTimeout(resolve, 300);
          } else {
            logoImg.onload = () => setTimeout(resolve, 300);
            logoImg.onerror = () => setTimeout(resolve, 300);
            // Timeout after 2 seconds
            setTimeout(resolve, 2000);
          }
        });
      } else {
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      // Convert to canvas then PDF
      const canvas = await html2canvas(pdfContainer, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Clean up
      document.body.removeChild(pdfContainer);

      // Generate PDF blob for preview
      const pdfBlobData = pdf.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlobData);

      // Store blob for potential upload
      setPdfBlob(pdfBlobData);

      // Show preview modal
      setPdfPreview(pdfUrl);
      setPreviewSchoolName(stats.schoolName);
      setShareableLink(null); // Reset shareable link

      setGeneratingPDF(null);
    } catch (err) {
      console.error("Error generating PDF:", err);
      alert("Error generating PDF: " + (err.message || "Unknown error"));
      setGeneratingPDF(null);
    }
  };

  const handleDownloadPDF = () => {
    if (pdfPreview && previewSchoolName) {
      const link = document.createElement("a");
      link.href = pdfPreview;
      link.download = `${previewSchoolName.replace(/[^a-z0-9]/gi, "_")}_Report.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleClosePreview = () => {
    if (pdfPreview) {
      URL.revokeObjectURL(pdfPreview);
      setPdfPreview(null);
      setPreviewSchoolName(null);
      setPdfBlob(null);
      setShareableLink(null);
      setTrackingLink(null);
      setTrackingId(null);
    }
  };

  const handleSaveToFirebase = async () => {
    if (!pdfBlob || !previewSchoolName) {
      alert("No PDF available to upload");
      return;
    }

    try {
      setIsUploading(true);
      setError(null);

      // Create a sanitized filename
      const sanitizedSchoolName = previewSchoolName.replace(/[^a-z0-9]/gi, "_");
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const fileName = `school-reports/${sanitizedSchoolName}_${timestamp}.pdf`;

      // Create storage reference
      const storageRef = ref(storage, fileName);

      // Upload PDF blob
      await uploadBytes(storageRef, pdfBlob, {
        contentType: "application/pdf",
      });

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);

      // Get current stats to store for comparison
      // Note: We regenerate stats here to ensure we have the latest data
      const statsForHash = await getSchoolStats(previewSchoolName, 0); // rank not needed for hash
      const statsHash = JSON.stringify({
        totalStudents: statsForHash.totalStudents,
        percentActiveStudents: statsForHash.percentActiveStudents,
        courseGrowthPercent: statsForHash.courseGrowthPercent,
        displayGradeImprovement: statsForHash.displayGradeImprovement,
        isGradeImprovement: statsForHash.isGradeImprovement,
        percentWillSaveMoney: statsForHash.percentWillSaveMoney,
      });

      // Prompt for email (optional) for tracking
      const email = prompt("Enter recipient email for tracking (optional, can be left blank):") || null;

      // Create tracking document in Firestore
      const trackingDocRef = await addDoc(collection(db, "pdfTracking"), {
        schoolName: previewSchoolName,
        fileName: fileName,
        storageUrl: downloadURL,
        statsHash: statsHash,
        email: email,
        createdAt: new Date(),
        views: [],
        totalViews: 0,
        uniqueViews: 0,
        lastViewed: null,
      });

      const trackingId = trackingDocRef.id;

      // Generate tracking URL (using the admin dashboard URL as base)
      const baseUrl = window.location.origin;
      const trackingUrl = email 
        ? `${baseUrl}/track-pdf/${trackingId}?email=${encodeURIComponent(email)}`
        : `${baseUrl}/track-pdf/${trackingId}`;

      setTrackingId(trackingId);
      setShareableLink(downloadURL);
      setTrackingLink(trackingUrl);
      alert("PDF uploaded successfully! Tracking link generated. Use the tracking link for emails.");
    } catch (err) {
      console.error("Error uploading PDF:", err);
      setError("Failed to upload PDF. Please try again.");
      alert("Error uploading PDF: " + (err.message || "Unknown error"));
    } finally {
      setIsUploading(false);
    }
  };

  const handleCopyLink = (linkType = 'tracking') => {
    const linkToCopy = linkType === 'tracking' ? trackingLink : shareableLink;
    if (linkToCopy) {
      navigator.clipboard.writeText(linkToCopy).then(() => {
        alert(`${linkType === 'tracking' ? 'Tracking' : 'Direct'} link copied to clipboard!`);
      }).catch((err) => {
        console.error("Failed to copy link:", err);
        alert("Failed to copy link. Please copy manually.");
      });
    }
  };

  const handleSchoolClick = (schoolName, index) => {
    const rank = index + 1;
    generateSchoolPDF(schoolName, rank);
  };

  return (
    <div className="schools-page">
      <div className="page-header">
        <div>
          <h1>Schools</h1>
          {lastUpdated && (
            <p className="last-updated">Last updated: {formatLastUpdated()}</p>
          )}
        </div>
        <button
          onClick={handleRefresh}
          className="refresh-button"
          disabled={isRefreshing}
        >
          {isRefreshing ? "⏳ Refreshing..." : "🔄 Refresh"}
        </button>
      </div>

      <div className="schools-grid">
        <div className="schools-card full-width">
          <h3 className="card-title">School Overview</h3>
          <div className="school-overview">
            <div className="overview-metric">
              <div
                className="metric-value large"
                style={{ color: COLORS.primary }}
              >
                {metrics.totalSchools}
              </div>
              <div className="metric-label">Total Schools</div>
            </div>
            <div className="overview-metric">
              <div className="metric-value" style={{ color: COLORS.success }}>
                {metrics.usersWithSchool}
              </div>
              <div className="metric-label">Users with School</div>
            </div>
            <div className="overview-metric">
              <div className="metric-value" style={{ color: COLORS.gray[500] }}>
                {metrics.usersWithoutSchool}
              </div>
              <div className="metric-label">Users without School</div>
            </div>
            <div className="overview-metric">
              <div className="metric-value" style={{ color: COLORS.info }}>
                {metrics.totalUsers}
              </div>
              <div className="metric-label">Total Users</div>
            </div>
          </div>
        </div>

        <div className="schools-card">
          <h3 className="card-title">Users by School</h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent, value }) => {
                    // Only show label if percentage is significant
                    if (percent < 0.05) return "";
                    return `${name}: ${value} (${(percent * 100).toFixed(0)}%)`;
                  }}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={pieColors[index] || entry.color}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  formatter={(value) => {
                    const item = pieData.find((d) => d.name === value);
                    return item ? `${value} (${item.value})` : value;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-chart">
              <p>No school data available</p>
            </div>
          )}
        </div>

        <div className="schools-card">
          <div className="card-title-header">
            <h3 className="card-title">Top Schools</h3>
            {metrics.schoolBreakdown.filter(school => school.name !== "Other Schools").length > 10 && (
              <button
                className="expand-button"
                onClick={() => setShowAllSchools(!showAllSchools)}
              >
                {showAllSchools
                  ? "Show Less"
                  : `Show All (${metrics.schoolBreakdown.filter(school => school.name !== "Other Schools").length})`}
              </button>
            )}
          </div>
          <div className="school-list">
            {metrics.schoolBreakdown.filter(school => school.name !== "Other Schools").length > 0 ? (
              <div
                className={`school-list-container ${
                  showAllSchools ? "expanded" : ""
                }`}
              >
                {(showAllSchools
                  ? metrics.schoolBreakdown.filter(school => school.name !== "Other Schools")
                  : metrics.schoolBreakdown.filter(school => school.name !== "Other Schools").slice(0, 10)
                ).map((school, index) => (
                  <div
                    key={index}
                    className={`school-item ${generatingPDF === school.name ? "generating" : ""}`}
                    onClick={() => handleSchoolClick(school.name, index)}
                    style={{ cursor: "pointer" }}
                    title="Click to generate PDF report"
                  >
                    <div className="school-rank">#{index + 1}</div>
                    <div className="school-info">
                      <div className="school-name">
                        {school.name}
                        {generatingPDF === school.name && (
                          <span style={{ marginLeft: "10px", fontSize: "12px", color: "#238a94" }}>
                            ⏳ Generating PDF...
                          </span>
                        )}
                      </div>
                      <div className="school-count">
                        {school.count} user{school.count !== 1 ? "s" : ""}
                      </div>
                    </div>
                    <div className="school-action" style={{ marginLeft: "auto", color: "#238a94", fontSize: "18px" }}>
                      📄
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-list">
                <p>No schools found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PDF Preview Modal */}
      {pdfPreview && (
        <div className="pdf-preview-modal" onClick={handleClosePreview}>
          <div className="pdf-preview-content" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-preview-header">
              <h2>PDF Preview: {previewSchoolName}</h2>
              <div className="pdf-preview-actions">
                <button onClick={handleDownloadPDF} className="pdf-download-button">
                  💾 Download PDF
                </button>
                <button 
                  onClick={handleSaveToFirebase} 
                  className="pdf-upload-button"
                  disabled={isUploading || shareableLink !== null}
                >
                  {isUploading ? "⏳ Uploading..." : shareableLink ? "✅ Uploaded" : "☁️ Save to Firebase"}
                </button>
                {trackingLink && (
                  <button onClick={() => handleCopyLink('tracking')} className="pdf-copy-button">
                    📋 Copy Tracking Link
                  </button>
                )}
                {shareableLink && !trackingLink && (
                  <button onClick={() => handleCopyLink('direct')} className="pdf-copy-button">
                    📋 Copy Link
                  </button>
                )}
                <button onClick={handleClosePreview} className="pdf-close-button">
                  ✕ Close
                </button>
              </div>
            </div>
            {trackingLink && (
              <div className="pdf-shareable-link-container" style={{ background: '#eff6ff', borderColor: '#3b82f6' }}>
                <div className="pdf-shareable-link-label" style={{ color: '#1e40af', fontWeight: 700 }}>
                  📧 Tracking Link (Use for Emails):
                </div>
                <div className="pdf-shareable-link-wrapper">
                  <input 
                    type="text" 
                    value={trackingLink} 
                    readOnly 
                    className="pdf-shareable-link-input"
                    onClick={(e) => e.target.select()}
                    style={{ borderColor: '#3b82f6' }}
                  />
                  <button onClick={() => handleCopyLink('tracking')} className="pdf-copy-link-button" style={{ background: '#3b82f6' }}>
                    📋 Copy
                  </button>
                </div>
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#1e40af' }}>
                  This link tracks opens, time viewed, and repeat views
                </div>
              </div>
            )}
            {shareableLink && (
              <div className="pdf-shareable-link-container">
                <div className="pdf-shareable-link-label">Direct Link (No Tracking):</div>
                <div className="pdf-shareable-link-wrapper">
                  <input 
                    type="text" 
                    value={shareableLink} 
                    readOnly 
                    className="pdf-shareable-link-input"
                    onClick={(e) => e.target.select()}
                  />
                  <button onClick={() => handleCopyLink('direct')} className="pdf-copy-link-button">
                    📋 Copy
                  </button>
                </div>
              </div>
            )}
            <div className="pdf-preview-iframe-container">
              <iframe
                src={pdfPreview}
                title="PDF Preview"
                className="pdf-preview-iframe"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Schools;
